import React, { useContext, useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import {
  fetchClientLanguagesAction,
  fetchTickmillProductsAction,
} from '@/store/clients/clients.actions'
import { fetchTickmillCompaniesAction } from '@/store/identity/identity.actions'
import { ClientsApiClient } from '@/utils/api/clientsApi'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { ChangeNotificationDto, NotificationDto } from 'api/Clients/Types/NotificationDto'
import { infoToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  getNotificationAudienceTypes,
  getNotificationBaseUrlTypes,
  getNotificationPlatforms,
  getNotificationProductStatuses,
  getNotificationTypes,
} from 'store/clientarea/clientarea.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { NotificationDetailsForm } from './NotificationDetailsForm'

interface Props {
  data?: NotificationDto
  forceRefresh(): void
}

const useMapStateToProps = () => ({
  countries: useAppSelector((state) => state.identity.countries),
  notificationAudienceTypes: useAppSelector((state) => state.clientArea.notificationAudienceTypes),
  notificationTypes: useAppSelector((state) => state.clientArea.notificationTypes),
  notificationPlatforms: useAppSelector((state) => state.clientArea.notificationPlatforms),
  notificationProductStatuses: useAppSelector(
    (state) => state.clientArea.notificationProductStatuses ?? []
  ),
  baseUrlTypes: useAppSelector((state) => state.clientArea.baseUrlTypes),
  navigate: useNavigate(),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  languages: useAppSelector((state) => state.clients.language),
  tickmillProducts: useAppSelector((state) => state.clients.tickmillProducts),
})

export const useNotificationDetailsFetch = () => {
  const mapStateToProps = useMapStateToProps()

  const { baseUrlTypes, tickmillCompanies, languages } = mapStateToProps
  const {
    notificationTypes,
    tickmillProducts,
    notificationAudienceTypes,
    notificationPlatforms,
    notificationProductStatuses,
  } = mapStateToProps

  const dispatch = useAppDispatch()
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  useEffect(() => {
    if (isEmpty(notificationAudienceTypes))
      dispatch(getNotificationAudienceTypes(clientAreaApiClient))
    if (isEmpty(notificationTypes)) dispatch(getNotificationTypes(clientAreaApiClient))
    if (isEmpty(notificationPlatforms)) dispatch(getNotificationPlatforms(clientAreaApiClient))
    if (isEmpty(notificationProductStatuses))
      dispatch(getNotificationProductStatuses(clientAreaApiClient))
    if (isEmpty(baseUrlTypes)) dispatch(getNotificationBaseUrlTypes(clientAreaApiClient))
    if (isEmpty(tickmillCompanies)) dispatch(fetchTickmillCompaniesAction(identityApiClient))
    if (isEmpty(languages)) dispatch(fetchClientLanguagesAction(clientsApiClient))
    if (isEmpty(tickmillProducts)) dispatch(fetchTickmillProductsAction(clientsApiClient))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return mapStateToProps
}

const useNotificationDetailsMutate = (props: Props) => {
  const { data, forceRefresh } = props

  const navigate = useNavigate()
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const setToast = useContext(ToastContext)

  const handleSubmit = async (notificationId: string, values: ChangeNotificationDto) => {
    try {
      if (data?.id) {
        await clientAreaApiClient.updateNotification(notificationId, values)
        forceRefresh()
        setToast(successToast('Notification updated successfully.'))
        await clientAreaApiClient.createRequestTranslationRequest()
        return
      } else {
        await clientAreaApiClient.createNotification({ ...values, images: data?.images ?? [] })
        forceRefresh()
        setToast(successToast('Notification created successfully.'))
        await clientAreaApiClient.createRequestTranslationRequest()
        navigate(links.marketingNotifications.url)
      }
    } catch {
      setToast(successToast('Error during update'))
    }
  }

  return { handleSubmit }
}

const NotificationDetailsPage: React.FC<Props> = (props) => {
  const { data, forceRefresh } = props
  const [cloneNotificationConfirmationModal, setCloneNotificationConfirmationModal] = useState<{
    data?: ChangeNotificationDto
    visible: boolean
  }>({ data: undefined, visible: false })
  const navigate = useNavigate()
  const setToast = useContext(ToastContext)

  const [isAuditLogVisible, setAuditLogVisible] = useState(false)

  const notificationDetailsFetch = useNotificationDetailsFetch()
  const { handleSubmit } = useNotificationDetailsMutate(props)

  const handleClone = (v: ChangeNotificationDto) => {
    setCloneNotificationConfirmationModal({ visible: true, data: v })
  }

  const handleSubmitClone = async () => {
    if (cloneNotificationConfirmationModal.data) {
      const clonedNotification: NotificationDto = {
        ...(data as NotificationDto),
        id: '',
        name: `${data?.name}-CLONED`,
        isEnabled: false,
      }

      navigate(
        generatePath(links.marketingNotifications.detailsUrl as string, {
          id: 'add-new',
        }),
        {
          state: {
            clonedNotification,
          },
        }
      )

      setToast(infoToast('Notification cloned successfully. Please check the details and save it.'))
      setCloneNotificationConfirmationModal({ visible: false, data: undefined })
    }
  }

  const handleCloneConfirmationModalClose = () =>
    setCloneNotificationConfirmationModal({ visible: false, data: undefined })

  const { baseUrlTypes, tickmillCompanies, languages } = notificationDetailsFetch
  const {
    countries,
    notificationTypes,
    notificationAudienceTypes,
    notificationPlatforms,
    notificationProductStatuses,
  } = notificationDetailsFetch
  return (
    <>
      {cloneNotificationConfirmationModal.data && (
        <ConfirmationModal
          contentText='This action will clone the notification. Do you wish to proceed?'
          yesButtonText='Proceed'
          onYes={handleSubmitClone}
          onCancel={handleCloneConfirmationModalClose}
          visible={cloneNotificationConfirmationModal.visible}
        />
      )}
      {isAuditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={isAuditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          notificationId={data?.id}
        />
      )}
      <NavigationHeader />
      <NotificationDetailsForm
        onSubmit={handleSubmit}
        setAuditLogModal={setAuditLogVisible}
        notification={data}
        countries={countries}
        notificationAudienceTypes={notificationAudienceTypes}
        notificationTypes={notificationTypes}
        notificationProductStatuses={notificationProductStatuses}
        tickmillCompanies={tickmillCompanies}
        baseUrlTypes={baseUrlTypes}
        onForceRefresh={forceRefresh}
        handleClone={handleClone}
        languages={languages}
        notificationPlatforms={notificationPlatforms}
      />
    </>
  )
}

export default NotificationDetailsPage
